import React, {forwardRef} from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {LabelBase} from './label-base';
import styles from './label.module.styl';

import type {NativeLabelProps} from './label-base';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';


const classNames = withCssModule(styles);

/**
 * HTML element variants that the `Label` component can render as its inner
 * element:
 * - `LABEL` (default) — renders a `<label>` element, suitable for form fields.
 *   Set `htmlFor` to associate it with an input.
 * - `DIV` — renders a `<div>`, useful for non-form label-like containers.
 * - `SPAN` — renders a `<span>`, useful for inline text labels.
 */
export const Tags = {
    LABEL: 'Tags.LABEL',
    DIV: 'Tags.DIV',
    SPAN: 'Tags.SPAN',
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TagsValues = [...Object.values(Tags)] as const;

/**
 * Union of `Tags` object values — the set of HTML element variant strings
 * accepted by the `slug` prop of `Label`.
 */
export type TagName = typeof TagsValues[number];

export interface LabelProps extends NativeLabelProps {
    /**
     * Label caption
     * @deprecated It will be removed in 6 months 09-20-2025. use children instead
     */
    caption?: ReactNode,
    /**
     * When `true` (default), truncates overflowing text with an ellipsis (`…`).
     * When `ellipsis` is `true` and no `title` is provided, the full text is also
     * shown in the browser's native tooltip on hover. When `false`, text wraps
     * normally.
     */
    ellipsis?: boolean,
    /**
     * Property for additional classes
     * @deprecated It will be removed in 6 months 09-20-2025. use className or rootProps.className instead
     */
    mix?: string,
    /**
     * React ref attached to the outer `<div>` root element (not the inner label
     * element). Use when you need to measure or observe the wrapper's dimensions.
     */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * Props spread onto the outer `<div>` root element that wraps the inner label
     * element. Use for attaching event listeners or additional class names to the
     * container without affecting the semantic `<label>` / `<span>` / `<div>`
     * inside.
     */
    rootProps?: ComponentPropsWithoutRef<'div'>,
    /**
     * HTML element rendered as the inner label element. Accepts a value from the
     * `Tags` object:
     * - `Tags.LABEL` (default) — `<label>`, use for form field associations.
     * - `Tags.SPAN` — `<span>`, for inline text labels.
     * - `Tags.DIV` — `<div>`, for block-level non-form labels.
     */
    slug?: TagName,
    /**
     * Native `title` attribute shown as a browser tooltip. When provided and
     * `ellipsis` is `true`, this value is used as the tooltip instead of the
     * automatic full-text tooltip, giving you control over the tooltip content.
     */
    title?: string,
}


/**
 * Renders a form label, inline text, or block label container using the
 * appropriate HTML element for the context.
 *
 * Wraps content in a `<div>` root and an inner `LabelBase` element whose tag
 * is controlled by the `slug` prop (`LABEL` → `<label>`, `SPAN` → `<span>`,
 * `DIV` → `<div>`). Supports optional text overflow ellipsis with a native
 * title tooltip when `ellipsis` is `true`.
 *
 * ## Accessibility
 * When using `slug="LABEL"` (default), set `htmlFor` to the `id` of the
 * associated form control so screen readers can announce the label.
 * See [WAI Labeling Controls](https://www.w3.org/WAI/tutorials/forms/labels/).
 */
export const Label = forwardRef<HTMLLabelElement, LabelProps>(function Label({
    caption,
    ellipsis = true,
    mix,
    slug = Tags.LABEL as TagName,
    title,
    children,
    className,
    rootRef,
    rootProps,
    ...props
}, ref) {

    const labelClasses = classNames(
        'label', mix, rootProps?.className,
        {
            label_label: Tags.LABEL === slug,
            label_text: Tags.SPAN === slug,
            label_ellipsis: ellipsis && !title,
        },
    );

    return (
        <div
            ref={rootRef}
            {...rootProps}
            className={labelClasses}>
            <LabelBase
                {...props}
                ref={ref}
                className={classNames(className, 'label__item')}
                ellipsis={ellipsis}
                title={title}>
                {caption || children}
            </LabelBase>
        </div>
    );
});

Label.displayName = 'Label';
